import { usePathname, useSearchParams } from 'next/navigation';
import { parseJsonParam } from '@/modules/common/url-params';
import { useRouter } from 'next/navigation';
import { DateRangePicker, Range, RangeFocus, RangeKeyDict } from 'react-date-range';
import { add, addDays } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import ko from 'date-fns/locale/ko';
import moment from 'moment';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import { twMerge } from 'tailwind-merge';
import useDeviceSize from '@/hooks/useDeviceSize';
import SweetAlert from 'sweetalert2';

// 달력에서 고를 수 있는 기간(오늘부터 며칠 뒤까지)의 기본값. 숙소에 예약가능일수가 설정돼 있으면 그 값을 쓴다.
// 마감 정보도 이 기간만큼 받아야 그 사이 만실이 열려 보이지 않는다.
export const BOOKING_WINDOW_DAYS = 90;

// 마감 정보를 받아야 하는 달 목록: 이번 달부터 달력 마지막 날이 속한 달까지.
export const bookingWindowMonths = (days: number = BOOKING_WINDOW_DAYS) => {
  const months: { year: number; month: number }[] = [];
  const last = moment().add(days, 'day').endOf('month');
  for (const m = moment().startOf('month'); m.isSameOrBefore(last); m.add(1, 'month')) {
    months.push({ year: m.year(), month: m.month() + 1 });
  }
  return months;
};

interface Props {
  className?: string;
  type?: 'date' | 'date_personnel' | string;
  onClose: () => void;
  clickReservationDate?: (data: { start: string; end: string; scrollPosition: number }) => void;
  disabledDates?: Date[];
  onOpenCalendar?: boolean;
  preserveScroll?: boolean; // 스크롤 위치 유지를 위한 prop 추가
  availableDays?: number; // 숙소 예약가능일수(없으면 BOOKING_WINDOW_DAYS)
}

const Calendar = ({
  className,
  type = 'date',
  disabledDates,
  clickReservationDate,
  onClose,
  onOpenCalendar,
  preserveScroll = false, // 기본값은 false
  availableDays = BOOKING_WINDOW_DAYS,
}: Props) => {
  const pathName = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const headerPeriod = parseJsonParam(searchParams?.get('header-period'));
  const headerHeadcount = parseJsonParam(searchParams?.get('header-headcount'));

  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();

  const [openCalendar, setOpenCalendar] = useState(onOpenCalendar ?? false);
  const [state, setState] = useState<Range[]>([]);

  useEffect(() => {
    onCloseCalendar();
  }, [onOpenCalendar]);

  useEffect(() => {
    setStartDate(headerPeriod != null ? new Date(headerPeriod?.start) : new Date());
    setEndDate(headerPeriod != null ? new Date(headerPeriod?.end) : addDays(new Date(), 1));

    setState([
      {
        startDate: headerPeriod != null ? new Date(headerPeriod?.start) : new Date(),
        endDate: headerPeriod != null ? new Date(headerPeriod?.end) : add(new Date(), { days: 1 }),
        key: 'selection',
      },
    ]);
  }, [searchParams]);

  const isDesktop = useDeviceSize();

  const handleSelect = (date: RangeKeyDict) => {
    setState([date.selection]);
  };

  // 마감 날짜는 "그날 밤"이 찼다는 뜻이다. 그날 체크인은 막지만, 그날 아침 퇴실은 막으면 안 된다.
  // 달력이 체크인(0)·퇴실(1) 중 무엇을 고르는 중인지. 여기서 들고 있어야 아래 판단에 쓸 수 있다.
  const [focusedRange, setFocusedRange] = useState<RangeFocus>([0, 0]);
  const selectingCheckOut = focusedRange[1] === 1;

  useEffect(() => {
    if (openCalendar) setFocusedRange([0, 0]); // 달력을 새로 열면 체크인부터 고른다
  }, [openCalendar]);

  const fullNights = useMemo(
    () => new Set((disabledDates ?? []).map((d) => moment(d).format('YYYY-MM-DD'))),
    [disabledDates],
  );

  const isDayDisabled = (day: Date) => {
    const checkIn = state[0]?.startDate;
    if (!selectingCheckOut || !checkIn) {
      return fullNights.has(moment(day).format('YYYY-MM-DD'));
    }
    // 퇴실일을 고르는 중: 두 날짜 사이의 밤이 모두 비어 있어야 고를 수 있다(앞 날짜를 누르면 달력이 순서를 바꿔준다).
    const [from, to] = day < checkIn ? [day, checkIn] : [checkIn, day];
    for (let night = from; moment(night).isBefore(to, 'day'); night = addDays(night, 1)) {
      if (fullNights.has(moment(night).format('YYYY-MM-DD'))) return true;
    }
    return false;
  };

  const onCloseCalendar = () => {
    setOpenCalendar(false);
  };

  const handleSetOpenCalendarClick = () => {
    // 현재 스크롤 위치 저장
    const currentScrollPosition = window.scrollY;

    setOpenCalendar(false);
    let newStartDate = '';
    let newEndDate = '';

    if (type === 'header') {
      newStartDate = moment(state[0].startDate).format('YYYY-MM-DD');
      newEndDate = moment(state[0].endDate).format('YYYY-MM-DD');

      if (newStartDate == newEndDate) {
        SweetAlert.fire({
          html: `데이유즈(공간임대) 예약 시 이용하실 날짜와 다음날을<br>함께 선택하여 예약해 주세요.
                        <br>(예: 4월 10일 이용 시 -> 4월 10일 ~ 4월 11일 선택)`,
          icon: 'warning',
          showConfirmButton: true,
        }).then((result) => {
          if (result.isConfirmed) {
            const today = new Date();

            newStartDate = moment(today).format('YYYY-MM-DD');
            newEndDate = moment(addDays(today, 1)).format('YYYY-MM-DD');

            // URL 변경 - 스크롤 위치 유지
            updateUrlWithScroll(
              JSON.stringify({
                start: newStartDate,
                end: newEndDate,
              }),
              JSON.stringify({
                adult: 1,
                kid: 0,
                pet: 0,
              }),
              currentScrollPosition,
            );
          }
        });
        return;
      } else {
        newStartDate = moment(state[0].startDate).format('YYYY-MM-DD');
        newEndDate = moment(state[0].endDate).format('YYYY-MM-DD');

        // URL 변경 - 스크롤 위치 유지
        updateUrlWithScroll(
          JSON.stringify({
            start: newStartDate,
            end: newEndDate,
          }),
          JSON.stringify({
            adult: 1,
            kid: 0,
            pet: 0,
          }),
          currentScrollPosition,
        );
      }
    } else {
      if (headerPeriod != null) {
        newStartDate = moment(headerPeriod?.start).format('YYYY-MM-DD');
        newEndDate = moment(headerPeriod?.end).format('YYYY-MM-DD');
      } else {
        newStartDate = moment(state[0].startDate).format('YYYY-MM-DD');
        newEndDate = moment(state[0].endDate).format('YYYY-MM-DD');
      }

      newStartDate = moment(state[0].startDate).format('YYYY-MM-DD');
      newEndDate = moment(state[0].endDate).format('YYYY-MM-DD');

      setStartDate(state[0].startDate);
      setEndDate(state[0].endDate);

      let adult = 1;
      let kid = 0;
      let pet = 0;

      if (headerHeadcount != null) {
        adult = headerHeadcount?.adult;
        kid = headerHeadcount?.kid;
        pet = headerHeadcount?.pet;
      }

      // URL 변경 - 스크롤 위치 유지
      updateUrlWithScroll(
        JSON.stringify({
          start: newStartDate,
          end: newEndDate,
        }),
        JSON.stringify({
          adult: adult,
          kid: kid,
          pet: pet,
        }),
        currentScrollPosition,
      );
    }

    // clickReservationDate 함수 호출
    // 스크롤 위치를 유지하기 위해 현재 위치 정보 전달
    if (clickReservationDate) {
      // 원본 clickReservationDate 함수를 호출하기 전에 스크롤 위치 저장
      clickReservationDate({
        start: newStartDate,
        end: newEndDate,
        scrollPosition: currentScrollPosition, // 스크롤 위치 정보 추가
      });
    }

    onClose();

    // 스크롤 위치 복원 (추가 안전장치)
    if (preserveScroll) {
      setTimeout(() => {
        window.scrollTo({
          top: currentScrollPosition,
          behavior: 'auto',
        });
      }, 100);
    }
  };

  // URL 업데이트 함수 - 스크롤 위치 유지
  const updateUrlWithScroll = (
    periodParam: string,
    headcountParam: string,
    scrollPosition: number,
  ) => {
    // URL 파라미터 변경 - replace 사용하여 히스토리에 추가하지 않음
    router.replace(
      `${pathName}?header-period=${periodParam}&header-headcount=${headcountParam}`,
      { scroll: false }, // Next.js의 scroll 옵션을 false로 설정
    );

    // setTimeout으로 스크롤 위치 복원
    if (preserveScroll) {
      setTimeout(() => {
        window.scrollTo({
          top: scrollPosition,
          behavior: 'auto',
        });
      }, 100);
    }
  };

  return (
    <div className={twMerge(`relative w-auto ${className}`)}>
      <div
        className={twMerge(`
          relative flex items-center border border-[#a4a4a4] cursor-pointer
           h-[46px] text-[#212121] width-[100%]
          ${type == 'header' ? 'rounded-[26px]' : 'rounded-l-[7px] rounded-r-[0px]'}
          ${className}`)}
        onClick={() => setOpenCalendar((prev: boolean) => !prev)}
      >
        <div className={`flex flex-1 items-center justify-center p-[10px] gap-[10px]`}>
          <img src='/calendar.png' alt='calendar' width={25} height={25} className='ml-[10px]' />
          <span className='text-[16px] whitespace-nowrap w-full mr-[10px] mobile:text-[14px]'>
            {type == 'header'
              ? '예약 날짜 선택'
              : type === 'date' && startDate && endDate
                ? startDate?.getFullYear() +
                  '.' +
                  Number(startDate?.getMonth() + 1) +
                  '.' +
                  startDate?.getDate() +
                  ' - ' +
                  endDate?.getFullYear() +
                  '.' +
                  Number(endDate?.getMonth() + 1) +
                  '.' +
                  endDate?.getDate()
                : 'yyyy.mm.dd - yyyy.mm.dd'}
          </span>
        </div>
      </div>
      {openCalendar && (
        <div className='absolute translate-x--50%] left-[-22px] top-[59px] z-10 bg-white border border-black'>
          <DateRangePicker
            locale={ko}
            editableDateInputs={true}
            // onChange={(item: any) => onChangeCalendar([item.selection])}
            onChange={handleSelect}
            // onChange={(item) => setState([item.selection])}
            moveRangeOnFirstSelection={false}
            ranges={state}
            months={isDesktop ? 2 : 1}
            direction='horizontal'
            // scroll={{ enabled: true }}
            minDate={moment().toDate()}
            maxDate={moment().add(availableDays, 'day').toDate()}
            displayMode='date'
            disabledDay={isDayDisabled}
            focusedRange={focusedRange}
            onRangeFocusChange={setFocusedRange}
            preventSnapRefocus={true}
            calendarFocus='forwards'
          />
          <div
            className='w-[100px] p-[7px] float-right text-center border border-black rounded-[25px] mb-[10px] mr-[10px] bg-[#000000] text-[#ffffff] cursor-pointer'
            onClick={handleSetOpenCalendarClick}
          >
            적용
          </div>
        </div>
      )}
    </div>
  );
};
export default Calendar;
